import { ArrowsOutSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import {
  BrandKitAsset,
  MediaAsset,
  StockPhoto,
  StockVideo,
} from "pages/media/types/";
import { TemplateType } from "../../../../types";
import { generateNewImage, generateNewVideo } from "../helpers";
import { useTranslate } from "@refinedev/core";
import { ButtonWithTooltip } from "../../ButtonWithTooltip";
import { Flex, message } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { DEFAULT_TEXT_COLOR } from "../../../../constants";
import { useMemo } from "react";
import { useRefreshAssetPath } from "#src/hooks";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../../../../store/useTemplateEditorStore";
import { useChangeLayer } from "../../../../hooks/useChangeLayer";
import { resolveVideoAssetIdToBrandKitPlaceholder } from "../../../../helpers/itemsHelper";
import { fetchAssetHasAudio } from "../../../../hooks/useAssetHasAudio";
import { useQueryClient } from "@tanstack/react-query";
import { useTemplateData } from "../../../../hooks/useTemplateData";

export type GalleryAsset =
  | BrandKitAsset
  | (Partial<MediaAsset> & { id: string });

const hasStringPath = (item: object): item is { path: string } =>
  "path" in item && typeof item.path === "string";

const hasStringAssetId = (item: object): item is { asset_id: string } =>
  "asset_id" in item && typeof item.asset_id === "string";

const hasStringId = (item: object): item is { id: string } =>
  "id" in item && typeof item.id === "string";

const getGalleryAssetId = (
  item: StockPhoto | StockVideo | GalleryAsset
): string | undefined => {
  if ("item_id" in item) return undefined;
  if (hasStringAssetId(item)) return item.asset_id;
  if (hasStringId(item)) return item.id;
  return undefined;
};

/** has_audio as listed in the asset library, undefined when the item doesn't carry it. */
const getListedHasAudio = (item: object): boolean | null | undefined => {
  if (!("has_audio" in item)) return undefined;
  const { has_audio } = item;
  return typeof has_audio === "boolean" ? has_audio : null;
};

// Shared by all cards: a slower earlier click must not overwrite a later one
let latestBackgroundClick = 0;

const getGalleryItemPath = (
  item: StockPhoto | StockVideo | GalleryAsset
): string | undefined => {
  if (!hasStringPath(item) || !item.path) return undefined;
  return item.path;
};

export const AssetControlsOverlay = ({
  item,
  isVideo,
  onUploadAsset,
  previewWidth,
  previewHeight,
  deleteButton,
  customAddToCanvas,
  selectionMode,
}: {
  item: StockPhoto | StockVideo | GalleryAsset;
  isVideo: boolean;
  onUploadAsset?: (value: StockPhoto | StockVideo) => Promise<
    | {
        assetId: string;
        path: string | null | undefined;
      }
    | undefined
  >;
  previewWidth: number;
  previewHeight: number;
  deleteButton?: React.ReactNode;
  customAddToCanvas?: (assetId: string, path: string) => void;
  /**
   * When true, renders a full-size click area instead of action buttons.
   * Designed for selection flows (e.g. picking a main image for a new slide).
   */
  selectionMode?: boolean;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const {
    setBackground,
    addImage,
    addVideo,
    setTransition,
    setIntro,
    setClosing,
    currentChapter,
  } = useTemplateEditorStore((store) => ({
    setBackground: store.setBackground,
    addImage: store.addImage,
    addVideo: store.addVideo,
    setTransition: store.setTransition,
    setIntro: store.setIntro,
    setClosing: store.setClosing,
    currentChapter: store.currentChapter,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const {
    brandKitData: { intro: brandKitIntro, closing: brandKitClosing },
  } = useTemplateData();

  const { newHighestLayerIndex } = useChangeLayer();
  const queryClient = useQueryClient();
  const templateEditorStoreApi = useTemplateEditorStoreApi();

  const isBackgroundOptionEnabled = useMemo(() => {
    if (isVideo) return true;
    return (
      currentChapter.type === TemplateType.TRANSITION ||
      currentChapter.type === TemplateType.CHAPTER
    );
  }, [currentChapter.type, isVideo]);

  const isStockAsset = (
    asset: StockPhoto | StockVideo | GalleryAsset
  ): asset is StockPhoto | StockVideo => "item_id" in asset;

  const galleryAssetId = useMemo(() => getGalleryAssetId(item), [item]);
  const galleryItemPath = useMemo(() => getGalleryItemPath(item), [item]);

  // Match MediaAssetPreview: resolve path via the asset detail API when the
  // gallery list item does not carry a usable URL yet.
  const { currentPath, refreshAssetUrl } = useRefreshAssetPath({
    assetId: galleryAssetId,
    path: galleryItemPath,
  });

  const unavailableAssetMessage = t(
    "components.templates.settings.imagesVideosTab.controls.AssetControlsOverlay.errorUnavailableAsset"
  );

  const resolveGalleryAssetPath = async (): Promise<string | undefined> => {
    if (currentPath) return currentPath;
    if (!galleryAssetId) return undefined;
    const result = await refreshAssetUrl();
    return result.data?.data.path;
  };

  const onAddAsset = async (): Promise<
    { assetId: string; path: string } | undefined
  > => {
    if (!onUploadAsset) {
      const assetId = galleryAssetId;
      if (!assetId) return undefined;
      const path = await resolveGalleryAssetPath();
      if (!path) return undefined;
      return { assetId, path };
    }

    if (!isStockAsset(item)) return;

    const newItem = await onUploadAsset(item);
    if (!newItem?.path) return;
    return { assetId: newItem.assetId, path: newItem.path };
  };

  const getNewVideoHasAudio = async (
    assetId: string
  ): Promise<boolean | null> => {
    // A stock video was just uploaded, so its soundtrack is not extracted yet
    if (isStockAsset(item)) return null;
    const listedHasAudio = getListedHasAudio(item);
    if (listedHasAudio !== undefined) return listedHasAudio;
    // Brand kit items don't carry has_audio
    return fetchAssetHasAudio(queryClient, assetId);
  };

  const handleAddAsBackground = async () => {
    const click = ++latestBackgroundClick;
    const result = await onAddAsset();
    if (click !== latestBackgroundClick) return;
    if (!result) {
      message.error(unavailableAssetMessage);
      return;
    }

    const { assetId, path } = result;

    // Convert to placeholder if asset matches brand kit intro/closing
    // Note: BK intro/closing can only be videos, so pictures won't match
    const resolvedAssetId = isVideo
      ? resolveVideoAssetIdToBrandKitPlaceholder(
          assetId,
          brandKitIntro,
          brandKitClosing
        )
      : assetId;

    const isIntroOrClosingVideo =
      isVideo &&
      (currentChapter.type === TemplateType.INTRODUCTION ||
        currentChapter.type === TemplateType.CLOSING);

    // Read before any setter: the setters below write to the open template.
    // Compare ids, not the chapter object: the template setup replaces it.
    const getOpenScene = () => {
      const { currentChapter, currentSlide } =
        templateEditorStoreApi.getState();
      return `${currentChapter.type}/${currentChapter.sectionId}/${currentSlide}`;
    };
    const sceneBefore = getOpenScene();
    const hasAudio = isIntroOrClosingVideo
      ? await getNewVideoHasAudio(assetId)
      : null;
    // The user opened another slide or picked another asset while has_audio was loading
    if (getOpenScene() !== sceneBefore || click !== latestBackgroundClick)
      return;

    const sceneUpdate = {
      enable: true,
      assetId: resolvedAssetId,
      assetPath: path,
      view: isVideo ? ("Video" as const) : ("Picture" as const),
    };
    if (currentChapter.type === TemplateType.TRANSITION) {
      setTransition(sceneUpdate);
    } else if (isIntroOrClosingVideo) {
      const setScene =
        currentChapter.type === TemplateType.INTRODUCTION
          ? setIntro
          : setClosing;
      // Video sound stays on only if the new video is known to have a soundtrack
      setScene(
        hasAudio === true
          ? sceneUpdate
          : { ...sceneUpdate, playVideoSound: false }
      );
    }

    if (isVideo) {
      setBackground({
        bodyColor: undefined,
        bodyView: "Video",
        bodyVideoAsset: resolvedAssetId,
        bodyVideo: path,
      });
    } else {
      setBackground({
        bodyColor: undefined,
        bodyView: "Picture",
        bodyImageAsset: assetId,
        bodyImage: path,
      });
    }

    addTemplateUpdate();
  };

  const handleAddToCanvas = async () => {
    const result = await onAddAsset();
    if (!result) {
      message.error(unavailableAssetMessage);
      return;
    }

    const { assetId, path } = result;

    // Selection-only flow (e.g. picking the main image for a brand-new slide):
    // hand the asset to the custom handler and stop. The current slide is
    // unchanged, so we must NOT call addTemplateUpdate() here — doing so marks
    // the selected template dirty and triggers a phantom autosave. The new
    // slide is created and persisted separately by the add-slide flow.
    if (customAddToCanvas) {
      customAddToCanvas(assetId, path);
      return;
    }

    const generateAction = isVideo ? generateNewVideo : generateNewImage;
    const addAction = isVideo ? addVideo : addImage;

    const newAsset = await generateAction(
      assetId,
      path,
      previewWidth,
      previewHeight,
      newHighestLayerIndex
    );

    if (!newAsset) {
      message.error(unavailableAssetMessage);
      return;
    }

    addAction(newAsset);
    addTemplateUpdate();
  };

  if (selectionMode) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={t(
          "components.templates.settings.imagesVideosTab.controls.AssetControlsOverlay.addToCanvas"
        )}
        onClick={(e) => {
          e.stopPropagation();
          void handleAddToCanvas();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            void handleAddToCanvas();
          }
        }}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 20,
          cursor: "pointer",
          background: "transparent",
        }}
      />
    );
  }

  return (
    <Flex
      justify="space-between"
      style={{
        position: "absolute",
        width: "90%",
        top: 6,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 20,
      }}
    >
      <Flex
        gap={2}
        style={{
          backgroundColor: `${DEFAULT_TEXT_COLOR}50`,
          borderRadius: theme.borderRadiusSM,
        }}
      >
        <ButtonWithTooltip
          title={t(
            "components.templates.settings.imagesVideosTab.controls.AssetControlsOverlay.addToCanvas"
          )}
          onClick={(e) => {
            e.stopPropagation();
            void handleAddToCanvas();
          }}
          icon={<PlusIcon weight="bold" />}
        />
        {!customAddToCanvas && isBackgroundOptionEnabled && (
          <ButtonWithTooltip
            title={t(
              "components.templates.settings.imagesVideosTab.controls.AssetControlsOverlay.setAsBackground"
            )}
            onClick={(e) => {
              e.stopPropagation();
              void handleAddAsBackground();
            }}
            icon={<ArrowsOutSimpleIcon weight="bold" />}
          />
        )}
      </Flex>
      {!customAddToCanvas && deleteButton && (
        // Stop propagation so deleting a library asset never bubbles to the
        // thumbnail's click handler (which replaces the selected item's asset).
        <span
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {deleteButton}
        </span>
      )}
    </Flex>
  );
};
